import { api } from '../../../services/api/client';
import type { Course, Lesson } from '../../../types';

// ---------- Public ----------
export async function fetchPublicCourses(): Promise<Course[]> {
  const { data } = await api.get<Course[]>('/public/courses');
  return data;
}

export async function fetchPublicCourse(slug: string): Promise<Course> {
  const { data } = await api.get<Course>(`/public/courses/${slug}`);
  return data;
}

export async function fetchPublicCourseById(courseId: string): Promise<Course> {
  const { data } = await api.get<Course>(`/public/courses/by-id/${courseId}`);
  return data;
}

// ---------- Member ----------
export async function fetchMyCourses(): Promise<Course[]> {
  const { data } = await api.get<Course[]>('/member/courses/my');
  return data;
}

export async function fetchCourseCatalog(): Promise<Course[]> {
  const { data } = await api.get<Course[]>('/member/courses/catalog');
  return data;
}

export async function fetchOwnedCourse(courseId: string): Promise<Course> {
  const { data } = await api.get<Course>(`/member/courses/${courseId}`);
  return data;
}

export async function fetchOwnedCourseLessons(courseId: string): Promise<Lesson[]> {
  const { data } = await api.get<Lesson[]>(`/member/courses/${courseId}/lessons`);
  return data;
}

export async function fetchOwnedLesson(courseId: string, lessonId: string): Promise<Lesson> {
  const { data } = await api.get<Lesson>(`/member/courses/${courseId}/lessons/${lessonId}`);
  return data;
}

export interface CompleteLessonResult {
  completed: boolean;
  lesson_id: string;
  next_lesson_id: string | null;
}

export async function completeLesson(courseId: string, lessonId: string): Promise<CompleteLessonResult> {
  const { data } = await api.post<CompleteLessonResult>(`/member/courses/${courseId}/lessons/${lessonId}/complete`);
  return data;
}

// ---------- Admin ----------
export interface CoursePayload {
  title: string;
  slug?: string;
  description?: string;
  thumbnail_url?: string;
  price?: number;
  currency?: string;
  access_type?: string;
  access_audiences?: string[];
  category?: string;
  is_published?: boolean;
}

export interface LessonPayload {
  title: string;
  description?: string;
  content_type?: string;
  video_provider?: string;
  video_url?: string;
  pdf_url?: string;
  pdf_name?: string;
  pdf_size?: number;
  allow_download?: boolean;
  sort_order?: number;
  is_published?: boolean;
}

export async function fetchAdminCourses(): Promise<Course[]> {
  const { data } = await api.get<Course[]>('/admin/courses');
  return data;
}

export async function createCourse(payload: CoursePayload): Promise<Course> {
  const { data } = await api.post<Course>('/admin/courses', payload);
  return data;
}

export async function updateCourse(courseId: string, payload: Partial<CoursePayload>): Promise<Course> {
  const { data } = await api.put<Course>(`/admin/courses/${courseId}`, payload);
  return data;
}

export async function fetchAdminLessons(courseId: string): Promise<Lesson[]> {
  const { data } = await api.get<Lesson[]>(`/admin/courses/${courseId}/lessons`);
  return data;
}

export async function createLesson(courseId: string, payload: LessonPayload): Promise<Lesson> {
  const { data } = await api.post<Lesson>(`/admin/courses/${courseId}/lessons`, payload);
  return data;
}

export async function updateLesson(courseId: string, lessonId: string, payload: Partial<LessonPayload>): Promise<Lesson> {
  const { data } = await api.put<Lesson>(`/admin/courses/${courseId}/lessons/${lessonId}`, payload);
  return data;
}

export async function grantCourseAccess(courseId: string, email: string): Promise<any> {
  const { data } = await api.post(`/admin/courses/${courseId}/access`, { email });
  return data;
}

export async function deleteCourse(courseId: string): Promise<any> {
  const { data } = await api.delete(`/admin/courses/${courseId}`);
  return data;
}

export function formatPrice(price: number, currency = 'IDR'): string {
  if (!price) return 'Gratis';
  try {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency, maximumFractionDigits: 0 }).format(price);
  } catch {
    return `${currency} ${price.toLocaleString('id-ID')}`;
  }
}

export const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'Semua Member', hint: 'Semua member yang sudah login' },
  { value: 'vip', label: 'Member VIP', hint: 'Member dengan status VIP aktif' },
  { value: 'webinar', label: 'Webinar Member', hint: 'User dengan role Webinar Member' },
  { value: 'workshop', label: 'Workshop Member', hint: 'User dengan role Workshop Member' },
  { value: 'purchase', label: 'Pembeli Course', hint: 'Bisa dibeli / di-grant admin' },
];

export function audienceLabels(values?: string[]): string {
  if (!values || values.length === 0) return 'Pembeli Course';
  return values
    .map((v) => AUDIENCE_OPTIONS.find((o) => o.value === v)?.label || v)
    .join(' · ');
}

export const ACCESS_TYPE_OPTIONS = [
  { value: 'public', label: 'Publik / Member Umum' },
  { value: 'vip', label: 'VIP' },
  { value: 'paid', label: 'Pembeli Course' },
];

export function accessTypeLabel(value?: string): string {
  return ACCESS_TYPE_OPTIONS.find((o) => o.value === value)?.label || 'Pembeli Course';
}
